import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PATH_ROOT } from '@/routes/paths';
import {
  PersonAdd as InviteIcon,
  Person as UserIcon,
} from '@mui/icons-material';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { MemberCard, InviteMembersModal, EditMemberModal } from '@/components/organization';
import { OrganizationOverviewCard } from '@/components/organization/OrganizationOverviewCard';
import { useDemoOrgData } from '@/hooks/organization/useDemoOrgData';
import { DEF_JAM_ORG_ID } from '@/mockData/common/demoFilters';
import { notifyDemoAction } from '@/utils/demoNotifications';
import { useOrgMembers, useOrgInvites, useOrgRoles } from 'src/hooks/organization';
import { useOrg } from 'src/contexts/OrgContext';
import { useDemoOrg } from 'src/contexts/DemoOrgContext';

interface OrganizationMembersProps {
  isDemo?: boolean;
}

const OrganizationMembers: React.FC<OrganizationMembersProps> = ({ isDemo = false }) => {
  const navigate = useNavigate();
  const { selectedOrgId } = useOrg();
  const demoOrgContext = useDemoOrg();
  const orgId = isDemo ? demoOrgContext?.selectedOrg?.orgId : selectedOrgId;
  const activeOrgId = isDemo ? (orgId ?? DEF_JAM_ORG_ID) : orgId;

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState<any | null>(null);

  const {
    members: apiMembers,
    updateMember,
    removeMember,
  } = useOrgMembers(isDemo ? undefined : activeOrgId);
  const { createInvite } = useOrgInvites(isDemo ? undefined : activeOrgId);
  const { customRoles } = useOrgRoles(isDemo ? undefined : activeOrgId);

  const { getMembersByOrgId, getOrganizationById } = useDemoOrgData();
  const demoMembers = getMembersByOrgId(activeOrgId ?? DEF_JAM_ORG_ID);
  const organization = getOrganizationById(activeOrgId ?? DEF_JAM_ORG_ID);
  const members = isDemo ? demoMembers : apiMembers;

  const handleInviteSubmit = async (
    email: string,
    roleId: string | undefined,
    customRoleId: string | undefined,
  ) => {
    if (isDemo) {
      notifyDemoAction(`Invite ${email}`, isDemo);
      return;
    }

    await createInvite(email, roleId, customRoleId);
  };

  const handleMemberUpdate = async (userId: string, roleId: any, customRoleId: string | undefined) => {
    if (isDemo) {
      notifyDemoAction('Update role for member', isDemo);
      setSelectedMember(null);
      return;
    }

    await updateMember(userId, roleId, customRoleId);
    setSelectedMember(null);
  };

  const handleMemberRemove = async (userId: string) => {
    if (isDemo) {
      notifyDemoAction('Remove member', isDemo);
      setSelectedMember(null);
      return;
    }

    await removeMember(userId);
    setSelectedMember(null);
  };

  const handleGoBack = () => {
    navigate(
      isDemo
        ? PATH_ROOT.demo.organizationManagement(activeOrgId || '')
        : PATH_ROOT.organization.management,
    );
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      <div className="mb-8">
        <button
          onClick={handleGoBack}
          className="mb-4 flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <h1 className="text-2xl font-semibold text-foreground">Organization Members</h1>
        <p className="mt-1 text-muted-foreground">
          Manage team members and their roles ({members.length} members)
        </p>
      </div>

      {organization && <OrganizationOverviewCard organization={organization} />}

      <div className="mb-6 flex items-center justify-between">
        <div />
        <Button
          variant="default"
          size="lg"
          onClick={() => setShowInviteModal(true)}
        >
          <InviteIcon className="mr-2 h-4 w-4" />
          Invite Members
        </Button>
      </div>

      <div className="space-y-4">
        {members.map((member: any) => (
          <MemberCard
            key={member.userId}
            userId={member.userId}
            displayName={member.userProfile?.displayName || member.displayName || 'Unknown User'}
            orgEmail={member.orgEmail}
            role={member.role}
            customRoleName={member.customRoleName}
            joinedDate={member.joinedDate}
            onAction={(_userId: string, action: 'edit' | 'remove') => {
              if (action === 'edit') {
                setSelectedMember(member);
                setShowEditModal(true);
              } else if (action === 'remove') {
                setSelectedMember(member);
                setShowEditModal(true);
              }
            }}
          />
        ))}
      </div>

      {members.length === 0 && (
        <div className="px-5 py-16 text-center text-muted-foreground">
          <UserIcon className="mx-auto mb-4 h-12 w-12 opacity-50 text-muted-foreground" />
          <h3 className="mb-2 text-lg text-muted-foreground">No members yet</h3>
          <p className="mb-6">Invite team members to start collaborating</p>
          <Button
            onClick={() => setShowInviteModal(true)}
            variant="default"
            size="lg"
          >
            <InviteIcon className="mr-2 h-4 w-4" />
            Invite Members
          </Button>
        </div>
      )}

      <div className="mt-8 flex gap-3">
        <Button
          variant="outline"
          size="lg"
          onClick={() => navigate(isDemo ? PATH_ROOT.demo.organizationRoles(activeOrgId || '') : PATH_ROOT.organization.roles)}
        >
          Manage Roles
        </Button>
        <Button
          variant="ghost"
          size="lg"
          onClick={handleGoBack}
        >
          Back to Management
        </Button>
      </div>

      {showInviteModal && (
        <InviteMembersModal
          isDemo={isDemo}
          orgId={activeOrgId}
          customRoles={customRoles}
          onClose={() => setShowInviteModal(false)}
          onSubmit={handleInviteSubmit}
        />
      )}
      {showEditModal && selectedMember && (
        <EditMemberModal
          isDemo={isDemo}
          orgId={activeOrgId}
          member={selectedMember}
          customRoles={customRoles}
          onClose={() => {
            setShowEditModal(false);
            setSelectedMember(null);
          }}
          onUpdateRole={handleMemberUpdate}
          onRemoveMember={handleMemberRemove}
        />
      )}
    </div>
  );
};

export default OrganizationMembers;
